// prettier-ignore
/*
 * predictive-search.js - the header search drawer, on the Tagalys SearchSuggestions
 * connector.
 *
 * Replaces the native Shopify Predictive Search implementation: that fetched
 * `routes.predictive_search_url` with `section_id=predictive-search`, injected the
 * returned HTML wholesale, and then made a SECOND request to sections/search-count
 * purely to read `search.results_count`, because Shopify's predictive_search object
 * exposes no total. Tagalys returns the total in the same response, so both requests
 * and that section are gone.
 *
 * Tagalys returns JSON, so cards are built client-side by tagalys-product-card.js.
 *
 * PRESERVED from the native implementation (they are drawer behaviour, not search
 * behaviour): keyboard navigation, the screen-reader live region, open/close, the
 * promo/default-state toggling, and the SearchForm base class contract.
 *
 * FALLBACK: when Tagalys is disabled (settings.tagalys_enabled off, or no server URL)
 * `onTagalysReady` never fires, `this.tagalysReady` stays false, and onChange falls back
 * to the native Shopify predictive-search path below - so the kill switch genuinely
 * reverts the drawer without a deploy.
 *
 * No user-facing strings here - all copy via window.tagalysCardStrings (set in
 * theme.liquid from Liquid `| t` calls).
 */
class PredictiveSearch extends SearchForm {
	constructor() {
		super();
		this.cachedResults = {};
		this.predictiveSearchResults = this.querySelector(
			"[data-predictive-search]",
		);
		this.allPredictiveSearchInstances =
			document.querySelectorAll("predictive-search");
		this.isOpen = false;
		this.abortController = new AbortController();
		this.searchTerm = "";
		this.tagalysReady = false;
		this.body = document.querySelector("body");
		this.header = document.querySelector(".shopify-section-header");
		this.headerBottom = document.querySelector(".header__bottom");
		this.promoModal = document.querySelector(".search__modal");
		this.collectionList = document.querySelector(".template-404__collections");
		this.headerInput = document.querySelector(".header__search .search__input");
		this.strings = window.tagalysCardStrings || {};
		this.searchStrings = this.strings.search || {};
		this.helpers = window.tagalysCardHelpers || {};
		this.calcHeight();
		this.setupEventListeners();
		this.setupTagalys();
	}

	/* ---------------------------------------------------------------- Tagalys */

	setupTagalys() {
		if (typeof window.onTagalysReady !== "function") return;

		window.onTagalysReady((Tagalys) => {
			/* One connector per page, shared by every <predictive-search> instance
			   (header drawer + the /search landing field), so a term typed in one is
			   not refetched by the other. */
			if (!window.tagalysSearchSuggestions) {
				window.tagalysSearchSuggestions =
					Tagalys.APIConnector.SearchSuggestions.new({
						params: {
							sections: {
								/* `products` is the only predefined section id. (Recent searches
								   are no longer shown at all - post-merge QA keeps the drawer's
								   default state on the trending links.)

								   `collections` (type=links) IS configured and was requested at
								   count 5, but the text link list it produced is not wanted in
								   the drawer - see renderSuggestions - so it is no longer
								   requested either, rather than fetched and dropped.

								   count 5: the drawer shows a single row of 5 product cards at
								   desktop (the grid is repeat(5, 1fr) >=1100). Was 10, which
								   painted a second row. */
								products: { include: ["total_count", "items"], count: 5 },
								/* Productive #205 - "Popular searches as you type". Tagalys
								   configured this custom section on 11 Sep 2026 (type
								   search_queries, items [{ query }]); it is rendered as a text
								   row above the product grid by buildQuerySuggestions. Safe to
								   request on a store where it is not configured yet (Billini US):
								   the API answers 200 and simply omits the section, verified
								   against api-r3 with an unknown section id. */
								popular_searches: { count: 5 },
							},
						},
						onSuccess: (response, helpers) => {
							this.renderSuggestions(response, helpers);
						},
						onFailure: () => {
							this.renderSuggestionsError();
						},
					});
			}
			this.connector = window.tagalysSearchSuggestions;
			this.tagalysReady = true;
		});
	}

	/* --------------------------------------------------------------- lifecycle */

	calcHeight() {
		if (document.querySelector(".predictive-search")) {
			if (!this.header) return;

			document.documentElement.style.setProperty(
				"--search-height",
				`calc(100vh - ${this.header.getBoundingClientRect().top}px)`,
			);
		}
	}

	setupEventListeners() {
		this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));
		this.input.addEventListener("focus", this.onFocus.bind(this));
		this.addEventListener("focusout", this.onFocusOut.bind(this));
		this.addEventListener("keyup", this.onKeyup.bind(this));
	}

	getQuery() {
		return this.input.value.trim();
	}

	onChange() {
		const newSearchTerm = this.getQuery();
		if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
			this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
		}

		this.updateSearchForTerm(this.searchTerm, newSearchTerm);
		this.searchTerm = newSearchTerm;

		if (!this.searchTerm.length) {
			/* QA #86: clearing the query left the LAST response's grid painted. close(true)
			   drops the open/results attributes (which also collapses the results lead-in
			   padding), but the Tagalys-injected markup in [data-predictive-search] was
			   never removed -- so a dead "N Results for ..." grid kept rendering, now with
			   no top padding and no footer. Abort anything in flight and empty the mount
			   so the drawer genuinely returns to its Default state. */
			this.abortController.abort();
			this.abortController = new AbortController();
			this.predictiveSearchResults.innerHTML = "";
			this.toggleNoResultsMessage(false);
			this.close(true);
			return;
		}

		this.getSearchResults(this.searchTerm);
		if (this.getQuery().length) {
			this.hidePromoBlock();
		} else {
			this.showPromoBlock();
		}
	}

	onFormSubmit(event) {
		/* Record the term BEFORE navigating so it appears in the shopper's recent
		   searches on the results page and on the next drawer open. */
		const term = this.getQuery();
		if (term.length && this.tagalysReady && this.connector) {
			try {
				this.connector.helpers.recordRecentSearch(
					this.connector.helpers.getEncodedQueryString({ query: term }),
				);
			} catch (error) {
				/* Never block the submit on the recent-search write. */
			}
		}

		if (
			!this.getQuery().length ||
			this.querySelector('[aria-selected="true"] a') ||
			event.submitter?.classList.contains("card__link")
		)
			event.preventDefault();
	}

	onFormReset(event) {
		super.onFormReset(event);
		if (super.shouldResetForm()) {
			this.searchTerm = "";
			this.abortController.abort();
			this.abortController = new AbortController();
			this.closeResults(true);
		}
	}

	onFocus() {
		const currentSearchTerm = this.getQuery();
		if (this.classList.contains("search-modal__form")) {
			if (this.headerBottom) {
				this.headerBottom.classList.add("header__bottom--visible");
			}
			if (this.promoModal) {
				this.promoModal.classList.remove("search__modal--hidden");
			}
			if (this.collectionList) {
				this.collectionList.classList.remove("hidden");
			}
		}

		if (!currentSearchTerm.length) {
			/* Empty input: the Liquid-rendered trending row IS the default state.
			   Post-merge QA removed the Tagalys recent/popular replacement that
			   used to overwrite it here. */
			return;
		}

		if (this.searchTerm !== currentSearchTerm) {
			this.onChange();
		} else if (this.getAttribute("results") === "true") {
			this.open();
			if (this.promoModal) {
				this.promoModal.classList.add("search__modal--hidden");
			}
			if (this.collectionList) {
				this.collectionList.classList.add("hidden");
			}
		} else {
			this.getSearchResults(this.searchTerm);
		}
	}

	onFocusOut() {
		if (this.classList.contains("search-modal__form")) {
			if (this.headerBottom) {
				this.headerBottom.classList.remove("header__bottom--visible");
			}
		}
		if (!this.classList.contains("search-modal__form")) {
			setTimeout(() => {
				if (!this.contains(document.activeElement)) this.close(true);
			});
		}
	}

	onBlur() {
		this.headerInput.blur();
	}

	onKeyup(event) {
		if (!this.getQuery().length) this.close(true);
		event.preventDefault();

		switch (event.code) {
			case "Enter":
				event.preventDefault();
				this.selectOption();
				break;
			case "Escape":
				this.close(true);
				this.closeResults(true);
				if (this.getQuery().length) {
					this.hidePromoBlock();
				} else {
					this.showPromoBlock();
				}
				break;
		}
	}

	updateSearchForTerm(previousTerm, newTerm) {
		const searchForTextElement = this.querySelector(
			"[data-predictive-search-search-for-text]",
		);
		const currentButtonText = searchForTextElement?.innerText;
		if (currentButtonText) {
			if (
				currentButtonText.matchAll(new RegExp(previousTerm, "g")).length > 1
			) {
				return;
			}
			const newButtonText = currentButtonText.replace(previousTerm, newTerm);
			searchForTextElement.innerText = newButtonText;
		}
		if (this.getQuery().length) {
			this.hidePromoBlock();
		} else {
			this.showPromoBlock();
		}
	}

	selectOption() {
		const selectedOption = this.querySelector(
			'[aria-selected="true"] a, button[aria-selected="true"]',
		);

		if (selectedOption) selectedOption.click();
	}

	/* ----------------------------------------------------------------- queries */

	getSearchResults(searchTerm) {
		this.setLiveRegionLoadingState();

		if (this.promoModal && this.classList.contains("search-modal__form")) {
			this.promoModal.classList.add("search__modal--hidden");
		}
		if (this.collectionList) {
			this.collectionList.classList.add("hidden");
		}

		if (this.tagalysReady && this.connector) {
			/* The SDK debounces updateQuery at 500ms by default, which matches the
			   previous handler's behaviour - no separate debounce needed. */
			this.connector.helpers.updateQuery(searchTerm);
			return;
		}

		/* Tagalys disabled or not yet ready: native Shopify predictive search. */
		this.getNativeSearchResults(searchTerm);
	}

	getNativeSearchResults(searchTerm) {
		const queryKey = searchTerm.replace(" ", "-").toLowerCase();

		if (this.cachedResults[queryKey]) {
			this.renderSearchResults(this.cachedResults[queryKey]);
			this.calcHeight();
			return;
		}

		fetch(
			`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`,
			{ signal: this.abortController.signal },
		)
			.then((response) => {
				if (!response.ok) {
					var error = new Error(response.status);
					this.close();
					throw error;
				}
				return response.text();
			})
			.then((text) => {
				const resultsMarkup = new DOMParser()
					.parseFromString(text, "text/html")
					.querySelector("#shopify-section-predictive-search")?.innerHTML;
				if (!resultsMarkup) return;
				this.allPredictiveSearchInstances.forEach(
					(predictiveSearchInstance) => {
						predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
					},
				);
				this.renderSearchResults(resultsMarkup);
				this.calcHeight();
			})
			.catch((error) => {
				if (error?.code === 20) return;
				this.close();
				throw error;
			});
	}

	/* ----------------------------------------------------------------- render */

	/* Single render path for every Tagalys response. */
	renderSuggestions(response, helpers) {
		const sections = (response && response.sections) || {};
		const products = sections.products || {};
		const items = products.items || [];
		const totalCount = products.total_count || 0;
		const term = this.getQuery();

		const container = document.createElement("div");

		if (!items.length) {
			/* QA #86 no-results (Figma 3822:48467 / 3822:48479): the message sits BELOW
			   the trending row with the Default chrome kept on screen -- not painted into
			   the results region above it. Clear any previous grid, surface the
			   Liquid-rendered message in the drawer body, and stand the Default state
			   back up (no open/results attributes, so the mobile footer stays hidden and
			   the header hugs its content exactly as in the Default state). */
			if (!this.getQuery().length) return;
			this.predictiveSearchResults.innerHTML = "";
			this.removeAttribute("results");
			this.closeResults();
			this.toggleNoResultsMessage(true);
			this.setLiveRegionText(
				this.helpers.interpolate(this.searchStrings.noResults, { terms: term }),
			);
			return;
		}

		const querySuggestions = this.buildQuerySuggestions(sections, helpers);
		if (querySuggestions) container.appendChild(querySuggestions);

		const heading = document.createElement("p");
		heading.className = "predictive-search__results-heading";
		heading.setAttribute("data-result-count-target", "");
		heading.setAttribute("data-search-terms", term);
		heading.textContent = this.helpers.pluralise(
			this.searchStrings.drawerResultsHeading,
			totalCount,
			{ terms: term },
		);
		container.appendChild(heading);

		const grid = document.createElement("div");
		grid.id = "predictive-search-results";
		grid.className = "predictive-search__grid";
		grid.setAttribute("role", "listbox");
		grid.setAttribute("tabIndex", "-1");
		container.appendChild(grid);

		/* No collection-suggestion link list: the drawer is a product-card
		   surface, and the text links rendered as a bare underlined column beneath
		   the grid. The `collections` section is no longer requested at all (see
		   the connector params above), so there is nothing to render here. */

		const viewAll = document.createElement("a");
		viewAll.className = "predictive-search__view-all";
		viewAll.setAttribute("data-view-all-target", "");
		viewAll.href = this.buildResultsHref(term, helpers);
		viewAll.textContent = this.strings.card?.viewAll || "";
		container.appendChild(viewAll);

		this.paintResults(container, totalCount, term);

		/* Cards are async (collection enrichment resolves badge rules first), so the
		   grid paints a moment after the chrome. */
		window
			.renderTagalysProductCards(items, {
				mediaSize: "portrait",
				mediaFit: "cover",
			})
			.then((cards) => {
				cards.forEach((card, index) => {
					const cell = document.createElement("div");
					cell.id = `predictive-search-option-product-${index + 1}`;
					cell.className = "predictive-search__grid-item";
					cell.setAttribute("role", "option");
					cell.setAttribute("aria-selected", "false");
					cell.appendChild(card);
					grid.appendChild(cell);
				});
				window.tagalysCardAfterRender();
			});
	}

	/* Productive #205 - text suggestions while typing. Every section of type
	   search_queries (today: Tagalys' `popular_searches`, items [{ query }]) is
	   folded into one de-duplicated row of links above the results heading, labelled
	   with general.search.popular_items ("Popular:"). The typed term itself is
	   dropped so the row never repeats the input. No Figma frame draws this state
	   (the Search Dropdown set has Default / Results / No results only), so the row
	   takes the Default state's trending-link type ramp - 12px/1.2, grey, weight 500,
	   sentence case - see component-predictive-search.css. Links go to the results
	   page for that query, recording it as a recent search like a form submit does. */
	buildQuerySuggestions(sections, helpers) {
		const term = this.getQuery().toLowerCase();
		const queries = [];
		Object.keys(sections || {}).forEach((id) => {
			const section = sections[id];
			if (!section || section.type !== "search_queries") return;
			(section.items || []).forEach((item) => {
				const query = item && (item.query || item.text);
				if (!query) return;
				const key = String(query).trim();
				if (!key || key.toLowerCase() === term) return;
				if (queries.some((existing) => existing.toLowerCase() === key.toLowerCase())) return;
				queries.push(key);
			});
		});
		if (!queries.length) return null;

		const wrapper = document.createElement("div");
		wrapper.className = "predictive-search__queries";

		const label = this.searchStrings.popularSearches || "";
		if (label) {
			const heading = document.createElement("p");
			heading.className = "predictive-search__queries-heading";
			heading.textContent = label;
			wrapper.appendChild(heading);
		}

		const list = document.createElement("ul");
		list.className = "predictive-search__queries-list list-unstyled";
		list.setAttribute("role", "list");
		queries.slice(0, 5).forEach((query) => {
			const item = document.createElement("li");
			item.className = "predictive-search__queries-item";
			const link = document.createElement("a");
			link.className = "predictive-search__query";
			link.href = this.buildResultsHref(query, helpers);
			link.textContent = query;
			link.addEventListener("click", () => {
				if (!this.connector) return;
				try {
					this.connector.helpers.recordRecentSearch(
						this.connector.helpers.getEncodedQueryString({ query }),
					);
				} catch (error) {
					/* Never block the navigation on the recent-search write. */
				}
			});
			item.appendChild(link);
			list.appendChild(item);
		});
		wrapper.appendChild(list);
		return wrapper;
	}

	renderSuggestionsError() {
		const container = document.createElement("div");
		const message = document.createElement("p");
		message.className = "search__no-results";
		message.textContent = this.helpers.interpolate(this.searchStrings.noResults, {
			terms: this.getQuery(),
		});
		container.appendChild(message);
		this.paintResults(container, 0, this.getQuery());
	}

	buildResultsHref(term, helpers) {
		const searchUrl =
			(window.tagalysConfig && window.tagalysConfig.searchUrl) || "/search";
		const connectorHelpers = helpers || this.connector?.helpers;
		if (connectorHelpers && typeof connectorHelpers.getEncodedQueryString === "function") {
			/* QA #86: the Tagalys helper returns a BARE query string ("q=boot", no leading
			   "?" -- see tagalys-facets.js), so the concatenation produced "/searchq=boot"
			   and the View-all CTA 404'd. Normalise the separator here rather than
			   trusting the helper's shape. */
			const qs = String(connectorHelpers.getEncodedQueryString({ query: term }) || "");
			if (!qs) return searchUrl;
			return searchUrl + (qs.startsWith("?") ? qs : "?" + qs.replace(/^&/, ""));
		}
		return `${searchUrl}?q=${encodeURIComponent(term)}`;
	}

	toggleNoResultsMessage(show) {
		document
			.querySelectorAll("#search-modal [data-search-no-results]")
			.forEach((element) => {
				element.hidden = !show;
			});
	}

	paintResults(container, totalCount, term) {
		/* QA #86: a slow response must not resurrect results for a query the shopper
		   has since cleared -- the Tagalys connector path doesn't ride the
		   abortController, so guard at the paint. */
		if (!this.getQuery().length) return;
		this.toggleNoResultsMessage(false);
		this.predictiveSearchResults.innerHTML = "";
		this.predictiveSearchResults.appendChild(container);
		this.setAttribute("results", true);
		this.setLiveRegionResults(totalCount, term);
		this.open();
		if (this.promoModal && this.classList.contains("search-modal__form")) {
			this.promoModal.classList.add("search__modal--hidden");
		}
		if (this.collectionList) {
			this.collectionList.classList.add("hidden");
		}
		this.paintViewAll(totalCount, term);
		this.calcHeight();
		this.handOffResultsCount(totalCount, term);
	}

	/* Post-merge QA part 2: the results page has no server-side source for the
	   Tagalys total, so its heading paints a pending placeholder until the JSON
	   lands. The drawer already holds that total for the typed query, so record it
	   here; main-search.liquid reads it back synchronously during parse and paints
	   the count before Tagalys answers again. Keyed by the normalised query so a
	   term edited after this paint (or a different query) never inherits it, and
	   stamped so the results page can ignore a stale value. Zero is not handed off:
	   the page's no-results hero belongs to its own response. */
	handOffResultsCount(totalCount, term) {
		if (!(totalCount > 0) || !term) return;
		try {
			window.sessionStorage.setItem(
				"tagalys:results-count",
				JSON.stringify({
					q: term.trim().replace(/\s+/g, " ").toLowerCase(),
					count: totalCount,
					at: Date.now(),
				}),
			);
		} catch (error) {
			/* Storage unavailable (private mode, quota): the page keeps its placeholder. */
		}
	}

	/* The mobile drawer-footer CTA is a sibling OUTSIDE <predictive-search> (it ships
	   with the drawer shell), hence the document-level query. Design-truth: the mobile
	   CTA is count-bearing, the desktop inline one is not - so only the href is synced
	   on the latter. */
	paintViewAll(totalCount, term) {
		const href = this.buildResultsHref(term);

		this.querySelectorAll("[data-view-all-target]").forEach((element) => {
			element.setAttribute("href", href);
		});

		document
			.querySelectorAll(".drawer__footer [data-view-all-target]")
			.forEach((element) => {
				if (!element.dataset.defaultLabel) {
					element.dataset.defaultLabel = element.textContent.trim();
				}
				element.setAttribute("href", href);
				if (totalCount > 0) {
					element.textContent = this.helpers.pluralise(
						this.searchStrings.drawerViewAllCount,
						totalCount,
					);
				} else {
					element.textContent = element.dataset.defaultLabel;
				}
			});
	}

	resetViewAllLabel() {
		const searchUrl =
			(window.tagalysConfig && window.tagalysConfig.searchUrl) || "/search";
		document
			.querySelectorAll(".drawer__footer [data-view-all-target]")
			.forEach((element) => {
				if (element.dataset.defaultLabel) {
					element.textContent = element.dataset.defaultLabel;
				}
				const term = this.getQuery();
				element.setAttribute(
					"href",
					term ? this.buildResultsHref(term) : searchUrl,
				);
			});
	}

	/* Native-path renderer, kept for the Tagalys-disabled fallback. */
	renderSearchResults(resultsMarkup) {
		this.predictiveSearchResults.innerHTML = resultsMarkup;
		this.setAttribute("results", true);
		this.removeAttribute("loading");
		this.open();
		if (this.promoModal && this.classList.contains("search-modal__form")) {
			this.promoModal.classList.add("search__modal--hidden");
		}
		if (this.collectionList) {
			this.collectionList.classList.add("hidden");
		}
	}

	/* ------------------------------------------------------------ live region */

	setLiveRegionLoadingState() {
		this.statusElement =
			this.statusElement || this.querySelector(".predictive-search-status");
		this.loadingText =
			this.loadingText || this.getAttribute("data-loading-text");

		this.setLiveRegionText(this.loadingText);
		this.setAttribute("loading", true);
	}

	setLiveRegionText(statusText) {
		if (!this.statusElement) return;
		this.statusElement.setAttribute("aria-hidden", "false");
		this.statusElement.textContent = statusText;

		setTimeout(() => {
			this.statusElement.setAttribute("aria-hidden", "true");
		}, 1000);
	}

	setLiveRegionResults(totalCount, term) {
		this.removeAttribute("loading");
		this.setLiveRegionText(
			this.helpers.pluralise(this.searchStrings.resultsWithCountAndTerm, totalCount, {
				terms: term,
			}),
		);
	}

	/* --------------------------------------------------------- promo / toggles */

	hidePromoBlock() {
		if (this.classList.contains("search-modal__form")) {
			if (this.promoModal) {
				this.promoModal.classList.add("search__modal--hidden");
			}
			if (this.collectionList) {
				this.collectionList.classList.add("hidden");
			}
		}
	}

	showPromoBlock() {
		if (this.classList.contains("search-modal__form")) {
			if (this.promoModal) {
				this.promoModal.classList.remove("search__modal--hidden");
			}
			if (this.collectionList) {
				this.collectionList.classList.remove("hidden");
			}
		}
	}

	open() {
		this.setAttribute("open", true);
		this.input.setAttribute("aria-expanded", true);
		this.isOpen = true;
		if (this.getQuery().length) {
			this.hidePromoBlock();
		} else {
			this.showPromoBlock();
		}
	}

	close(clearSearchTerm = false) {
		this.closeResults(clearSearchTerm);
		this.isOpen = false;
	}

	closeResults(clearSearchTerm = false) {
		if (clearSearchTerm) {
			this.input.value = "";
			this.removeAttribute("results");
			this.resetViewAllLabel();
			this.toggleNoResultsMessage(false);
		}
		const selected = this.querySelector('[aria-selected="true"]');

		if (selected) selected.setAttribute("aria-selected", false);

		this.input.setAttribute("aria-activedescendant", "");
		this.removeAttribute("loading");
		this.removeAttribute("open");
		this.input.setAttribute("aria-expanded", false);
		this.predictiveSearchResults.removeAttribute("style");
		if (this.getQuery().length) {
			this.hidePromoBlock();
		} else {
			this.showPromoBlock();
		}
	}
}

customElements.define("predictive-search", PredictiveSearch);
